import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { AirplaneTilt, SignOut, User, LockKey, Buildings, List } from "@phosphor-icons/react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "./ui/dropdown-menu";

export default function TopBar() {
  const { profile, signOut } = useAuth();
  const nav = useNavigate();

  const onLogout = async () => {
    await signOut();
    nav("/login");
  };

  const roleLabel = profile?.role === "SENDER" ? "NEXT-FLIGHT CARGO · SENDER PORTAL" :
    profile?.role === "CAP" ? "NEXT-FLIGHT CARGO · CAPTAIN" :
    profile?.role === "RID" ? "NEXT-FLIGHT CARGO · RIDER" :
    "NEXT-FLIGHT CARGO · CONTROL TOWER";

  return (
    <header className="border-b border-[hsl(var(--border-strong))] bg-white">
      <div className="max-w-[1600px] mx-auto px-4 md:px-6 h-14 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <AirplaneTilt size={20} weight="bold" className="shrink-0" />
          <span className="heading text-base tracking-tight shrink-0">AIR12</span>
          <span className="overline ml-3 hidden lg:inline">{roleLabel}</span>
        </div>

        {/* Desktop cluster — unchanged, shown at lg+ where the full row genuinely fits */}
        {profile && (
          <div className="hidden lg:flex items-center gap-3">
            <span className="overline" data-testid="topbar-role">{profile.role.replace(/_/g, " ")}</span>
            <span className="id-chip" data-testid="topbar-user-id">
              {profile.public_staff_id || profile.public_sender_id || "—"}
            </span>
            <span className="text-sm flex items-center gap-1.5" data-testid="topbar-email">
              <User size={14} weight="duotone" />
              {profile.email}
            </span>
            {profile.role === "SENDER" && (
              <Link to="/sender/company" className="btn-ghost" data-testid="company-details-link">
                <Buildings size={14} /> Company details
              </Link>
            )}
            <Link to="/account/security" className="btn-ghost" data-testid="account-security-link">
              <LockKey size={14} /> Security
            </Link>
            <button className="btn-ghost" onClick={onLogout} data-testid="logout-button">
              <SignOut size={14} /> Sign out
            </button>
          </div>
        )}

        {/* Compact mobile/tablet menu — exposes the SAME existing actions, no new destinations */}
        {profile && (
          <div className="lg:hidden flex items-center gap-2 min-w-0">
            <span className="overline hidden sm:inline" data-testid="topbar-role-mobile">{profile.role.replace(/_/g, " ")}</span>
            <DropdownMenu>
              <DropdownMenuTrigger className="btn-ghost" data-testid="topbar-mobile-menu-btn" aria-label="Menu">
                <List size={18} weight="bold" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel className="flex flex-col gap-0.5">
                  <span className="text-sm font-semibold" data-testid="mobile-menu-email">{profile.email}</span>
                  <span className="overline" data-testid="mobile-menu-role">{profile.role.replace(/_/g, " ")}</span>
                  <span className="id-chip w-fit mt-1" data-testid="mobile-menu-user-id">
                    {profile.public_staff_id || profile.public_sender_id || "—"}
                  </span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {profile.role === "SENDER" && (
                  <DropdownMenuItem asChild>
                    <Link to="/sender/company" data-testid="company-details-link-mobile">
                      <Buildings size={14} className="mr-2" /> Company details
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem asChild>
                  <Link to="/account/security" data-testid="account-security-link-mobile">
                    <LockKey size={14} className="mr-2" /> Security
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onLogout} data-testid="logout-button-mobile">
                  <SignOut size={14} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>
    </header>
  );
}
